/**
 * Persistence for the save document (tasks 13.1, 13.4, 13.7).
 *
 * A *driver* owns the bytes; the store owns what they mean. The driver is
 * asynchronous because IndexedDB is, and the store's `save` is not, because it
 * is called from a synchronous state update after every action in a run and
 * must never make the interface wait on a transaction. Writes are therefore
 * fire-and-forget and **coalesced**: a burst of actions produces one queued
 * write of the latest document rather than a queue of stale ones landing out of
 * order.
 *
 * Storage being unavailable is a supported state, not an error. A private
 * window with IndexedDB denied still plays; it just warns once that nothing
 * will be kept (`save-system` spec, "Storage unavailable").
 *
 * Nothing here ever overwrites data it could not read. A corrupt document and a
 * document from a newer build both start a fresh *session* and leave what is on
 * disk exactly where it is — the alternative destroys a save on the one launch
 * where the player most needs it back.
 */
import { get, openDatabase, put, remove, transact } from '../idb.js';
import { emptyDocument } from './document.js';
import { migrate, SaveTooNewError, SaveUnreadableError } from './migrations.js';
import type { SaveDocument } from './types.js';

export interface SaveDriver {
  /** False when nothing written this session will survive a reload. */
  readonly available: boolean;
  /** What is behind it, for a test and for a diagnostic to name. */
  readonly kind: 'indexeddb' | 'memory';
  read(): Promise<string | null>;
  write(text: string): Promise<void>;
  clear(): Promise<void>;
}

export const SAVE_KEY = 'managing-up:save';
export const DB_NAME = 'managing-up';
export const DB_VERSION = 1;
export const STORE_NAME = 'save';

/**
 * IndexedDB, through one object store holding one document.
 *
 * One record rather than a store per profile or a store for runs: the document
 * is written whole on every action anyway — a run *is* most of its bytes — and
 * a single record makes the write atomic, which is what makes crash recovery a
 * property of the storage layer rather than of a reconciliation step.
 *
 * The database is opened eagerly and its failure is a resolved `null`, not a
 * rejection, so an unavailable database degrades to an unavailable driver
 * rather than an unhandled rejection on boot.
 */
export function indexedDbDriver(
  // `null` rather than `undefined` for "there is no IndexedDB here": an
  // explicit `undefined` argument would fall through to the default and quietly
  // pick up the global, which is exactly the case a caller is trying to exclude.
  factory: IDBFactory | null = globalThis.indexedDB ?? null,
): SaveDriver {
  if (factory === null) return memoryDriver();

  const database = openDatabase(factory, {
    name: DB_NAME,
    version: DB_VERSION,
    stores: [{ name: STORE_NAME }],
  });

  const withStore = async <T>(
    run: (store: IDBObjectStore) => Promise<T>,
    mode: IDBTransactionMode,
    fallback: T,
  ): Promise<T> => {
    try {
      const db = await database;
      return db === null ? fallback : await transact(db, STORE_NAME, mode, run);
    } catch {
      // A quota refusal, a database deleted from under us, a transaction
      // aborted by a browser reclaiming space. None of them may take the run
      // down: the player has been told this device may not keep progress.
      return fallback;
    }
  };

  return {
    available: true,
    kind: 'indexeddb',
    read: () =>
      withStore(
        async (store) => (await get<string>(store, SAVE_KEY)) ?? null,
        'readonly',
        null as string | null,
      ),
    write: (text) =>
      withStore(
        async (store) => {
          await put(store, text, SAVE_KEY);
        },
        'readwrite',
        undefined,
      ),
    clear: () =>
      withStore(
        async (store) => {
          await remove(store, SAVE_KEY);
        },
        'readwrite',
        undefined,
      ),
  };
}

/** Keeps a document for the session only. Reports itself as unavailable. */
export function memoryDriver(initial: string | null = null): SaveDriver {
  let held = initial;
  return {
    available: false,
    kind: 'memory',
    read: () => Promise.resolve(held),
    write: (text) => {
      held = text;
      return Promise.resolve();
    },
    clear: () => {
      held = null;
      return Promise.resolve();
    },
  };
}

/**
 * The driver the application runs on: IndexedDB where it exists, and a
 * session-only one where it does not.
 *
 * No `localStorage` tier in between. A document carrying a whole `GameState`
 * outgrows the 5MB quota that browsers give `localStorage` faster than anyone
 * would expect, and half-persisting a save is a worse failure than not
 * persisting one — the player is told plainly which of the two they have.
 */
export function defaultDriver(): SaveDriver {
  return indexedDbDriver();
}

export interface LoadedSave {
  readonly doc: SaveDocument;
  /** True when nothing written this session will survive a reload. */
  readonly ephemeral: boolean;
  /** Set when stored data could not be read and a fresh document was started. */
  readonly recovered: boolean;
  /** Why it could not be read, for the warning the player sees. */
  readonly recoveredBecause: string | null;
  /** The versions whose migration steps ran on load, in order. */
  readonly migratedFrom: number | null;
}

export interface SaveStore {
  load(now: string): Promise<LoadedSave>;
  /** Queues a write of `doc`. Returns immediately; failures are swallowed. */
  save(doc: SaveDocument): void;
  reset(now: string): SaveDocument;
  /** Resolves once every queued write has settled. For tests and for teardown. */
  flush(): Promise<void>;
}

export function openSaveStore(driver: SaveDriver): SaveStore {
  // One write in flight at a time, and only ever the newest document pending
  // behind it. Two hundred actions in a mission must not become two hundred
  // queued transactions racing to be last.
  let inFlight: Promise<void> = Promise.resolve();
  let pending: SaveDocument | null = null;
  let draining = false;

  const drain = (): void => {
    if (draining) return;
    draining = true;
    inFlight = inFlight
      .then(async () => {
        while (pending !== null) {
          const next = pending;
          pending = null;
          try {
            await driver.write(JSON.stringify(next));
          } catch {
            // A full or denied quota must not take the run down with it.
          }
        }
      })
      .finally(() => {
        draining = false;
      });
  };

  const queue = (doc: SaveDocument): void => {
    pending = doc;
    drain();
  };

  const fresh = (now: string, recoveredBecause: string | null, writable: boolean): LoadedSave => {
    const doc = emptyDocument(now);
    if (writable) queue(doc);
    return {
      doc,
      // A refused save is not overwritten, so nothing this session writes will
      // be kept either — which is exactly what `ephemeral` means.
      ephemeral: !driver.available || !writable,
      recovered: recoveredBecause !== null,
      recoveredBecause,
      migratedFrom: null,
    };
  };

  return {
    load: async (now) => {
      let text: string | null = null;
      try {
        text = await driver.read();
      } catch {
        return fresh(now, 'Saved data on this device could not be read.', false);
      }
      if (text === null) return fresh(now, null, true);

      let raw: unknown;
      try {
        raw = JSON.parse(text) as unknown;
      } catch {
        return fresh(now, 'Saved data on this device is not valid JSON.', false);
      }

      try {
        const { doc, from, applied } = migrate(raw);
        // Written straight back at the current version, so the migration is
        // paid for once rather than on every launch.
        if (applied.length > 0) queue(doc);
        return {
          doc,
          ephemeral: !driver.available,
          recovered: false,
          recoveredBecause: null,
          migratedFrom: applied.length > 0 ? from : null,
        };
      } catch (cause) {
        if (cause instanceof SaveTooNewError) return fresh(now, cause.message, false);
        if (cause instanceof SaveUnreadableError) return fresh(now, cause.message, false);
        return fresh(now, 'Saved data on this device could not be read.', false);
      }
    },

    save: queue,

    reset: (now) => {
      const doc = emptyDocument(now);
      pending = null;
      inFlight = inFlight.then(() => driver.clear()).catch(() => undefined);
      queue(doc);
      return doc;
    },

    flush: async () => {
      // Twice: a write queued *while* the first drain was running is picked up
      // by that same drain, and awaiting the chain again catches it.
      await inFlight;
      await inFlight;
    },
  };
}
